// Browser Action side-panel editor — the deterministic (non-AI) browser
// node. Steps-first: the user enters a Webpage URL and records the step
// sequence in the opened tab (hovering picks an element, then its balloon chooses the
// action — click / type / extract / …). Recorded steps are listed here
// with per-step text, delay and re-pick controls. A node with a URL and
// no steps simply opens the page at run time.

const React = require('react');
const { useEffect, useState } = React;
const { createElement: h } = React;
const apiClient = require('../lib/apiClient');
const VariableAutocompleteInput = require('./VariableAutocompleteInput');

const STEP_LABELS = {
  click: 'Click',
  clickIfText: 'Conditional Click',
  type: 'Type',
  typeEnter: 'Type + Enter',
  pressEnter: 'Press Enter',
  extract: 'Extract',
  screenshot: 'Screenshot',
  scroll: 'Scroll',
  aiPointer: 'AI Pointer'
};

const STEP_ICON_SHAPES = {
  click: [['path', { d: 'M5 3l13 8-6 2 4 6-3 2-4-6-4 4z' }]],
  type: [
    ['rect', { x: 3, y: 5, width: 18, height: 14, rx: 2 }],
    ['path', { d: 'M7 9h.01M11 9h.01M15 9h.01M7 13h.01M11 13h.01M15 13h2M7 16h10' }]
  ],
  pressEnter: [
    ['path', { d: 'M19 5v6a4 4 0 0 1-4 4H5' }],
    ['path', { d: 'm9 11-4 4 4 4' }]
  ],
  extract: [
    ['path', { d: 'M12 3v12M7 10l5 5 5-5M5 20h14' }]
  ],
  screenshot: [
    ['path', { d: 'M8 5l1.5-2h5L16 5' }],
    ['rect', { x: 3, y: 5, width: 18, height: 16, rx: 2 }],
    ['circle', { cx: 12, cy: 13, r: 3 }]
  ],
  scroll: [
    ['rect', { x: 7, y: 2, width: 10, height: 20, rx: 5 }],
    ['path', { d: 'M12 6v5M10 9l2 2 2-2' }]
  ],
  aiPointer: [
    ['path', { d: 'M4 6l11 7-5 1.5 3.5 5-2.5 1.5-3.5-5-3.5 4z' }],
    ['path', { d: 'M17 2l1 2.5L20.5 5.5 18 6.5 17 9l-1-2.5L13.5 5.5 16 4.5z' }]
  ]
};
STEP_ICON_SHAPES.typeEnter = STEP_ICON_SHAPES.type;
STEP_ICON_SHAPES.clickIfText = STEP_ICON_SHAPES.click;

function StepActionIcon(props) {
  const action = String(props.action || 'click');
  const shapes = STEP_ICON_SHAPES[action] || STEP_ICON_SHAPES.click;
  return h('span', {
    className: 'wf-ba-step-icon',
    title: STEP_LABELS[action] || action,
    'aria-hidden': 'true'
  }, h('svg', {
    className: 'wf-ba-step-icon-svg',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    focusable: 'false'
  }, shapes.map(function (shape, index) {
    return h(shape[0], Object.assign({ key: index }, shape[1]));
  })));
}

// Same non-blocking poll pattern as BrowserRunRecorderEditor: hint-only,
// the node still saves without a connection.
function useConnectionStatus(pollMs) {
  const [status, setStatus] = useState({ connected: false, checked: false });
  useEffect(function () {
    let cancelled = false;
    function tick() {
      apiClient.request('/browser-action/status', { method: 'GET' })
        .then(function (res) {
          if (cancelled) return;
          setStatus({ connected: !!(res && res.connected), checked: true });
        })
        .catch(function () {
          if (cancelled) return;
          setStatus({ connected: false, checked: true });
        });
    }
    tick();
    const iv = setInterval(tick, pollMs || 4000);
    return function () { cancelled = true; clearInterval(iv); };
  }, [pollMs]);
  return status;
}

// A numbered section: green badge, label, optional REQUIRED pill, then the
// control(s) indented underneath. `n` is the running step number the caller
// assigns over whatever rows are currently visible (numbers stay 1..N with
// no gaps as conditional rows appear/disappear).
function NumberedSection(props) {
  return h('div', { className: 'wf-ba-section' },
    h('div', { className: 'wf-ba-section-head' },
      h('span', { className: 'wf-ba-badge' }, props.n),
      h('span', { className: 'wf-ba-section-label' }, props.label),
      props.required ? h('span', { className: 'wf-ba-required' }, 'Required') : null
    ),
    h('div', { className: 'wf-ba-section-body' }, props.children)
  );
}

function describeScrollStep(step) {
  const mode = String(step.scrollMode || 'viewport');
  if (mode === 'bottom') return 'to page bottom';
  const direction = String(step.scrollDirection || 'down');
  const configuredPixels = Number(step.scrollDistancePx);
  const pixels = String(step.scrollDistancePx == null ? '' : step.scrollDistancePx).trim() !== ''
    && Number.isFinite(configuredPixels) && configuredPixels >= 0
    ? configuredPixels
    : 600;
  return direction + (mode === 'pixels'
    ? ' ' + pixels + ' px'
    : ' one viewport');
}

function ScrollStepFields(props) {
  const step = props.step || {};
  const mode = String(step.scrollMode || 'viewport');
  const direction = String(step.scrollDirection || 'down');
  const change = props.onChange || function () {};

  return h('div', { className: 'wf-ba-scroll-grid' },
    h('label', { className: 'wf-ba-scroll-field' },
      h('span', null, 'Direction'),
      h('select', {
        className: 'wf-params-input',
        value: direction,
        disabled: mode === 'bottom',
        onChange: function (e) { change({ scrollDirection: e.target.value }); }
      },
        h('option', { value: 'down' }, 'Down'),
        h('option', { value: 'up' }, 'Up')
      )
    ),
    h('label', { className: 'wf-ba-scroll-field' },
      h('span', null, 'Distance'),
      h('select', {
        className: 'wf-params-input',
        value: mode,
        onChange: function (e) { change({ scrollMode: e.target.value }); }
      },
        h('option', { value: 'viewport' }, 'One viewport'),
        h('option', { value: 'pixels' }, 'Pixels'),
        h('option', { value: 'bottom' }, 'To page bottom')
      )
    ),
    mode === 'pixels' ? h('label', { className: 'wf-ba-scroll-field' },
      h('span', null, 'Pixels'),
      h('input', {
        className: 'wf-params-input',
        type: 'number',
        min: 0,
        value: step.scrollDistancePx !== undefined ? step.scrollDistancePx : 600,
        onChange: function (e) { change({ scrollDistancePx: e.target.value }); }
      })
    ) : null,
    h('label', { className: 'wf-ba-scroll-field' },
      h('span', null, 'Scroll duration (ms)'),
      h('input', {
        className: 'wf-params-input',
        type: 'number',
        min: 0,
        max: 60000,
        value: step.scrollDurationMs !== undefined ? step.scrollDurationMs : 600,
        title: 'Lower is faster. Use 0 to jump instantly.',
        onChange: function (e) { change({ scrollDurationMs: e.target.value }); }
      })
    )
  );
}

function BrowserActionEditor(props) {
  const paramValues   = props.paramValues || {};
  const onParamChange = props.onParamChange || function () {};
  const variableNodes = props.variableNodes || [];
  const nodeId        = props.nodeId || '';
  const conn = useConnectionStatus(4000);
  const [picking, setPicking] = useState(false);
  const [pickingStep, setPickingStep] = useState(-1); // step index being re-picked
  const [pickError, setPickError] = useState('');
  const [draggedStep, setDraggedStep] = useState(-1);
  const [dragOverStep, setDragOverStep] = useState(-1);

  const url           = String(paramValues.url || '');
  const tabMode       = String(paramValues.tabMode || 'new');
  const continueFrom  = String(paramValues.continueFrom || '');
  const closeTab      = String(paramValues.closeTab || '') === 'true';

  // The node's behavior IS the recorded step sequence (the old single
  // action/selector fields still run for legacy saved nodes but are no
  // longer editable here).
  let steps = [];
  try {
    const parsed = JSON.parse(String(paramValues.steps || '[]'));
    if (Array.isArray(parsed)) steps = parsed;
  } catch (e) { /* corrupted steps param — treat as none */ }

  function saveSteps(next) {
    onParamChange('steps', next.length ? JSON.stringify(next) : '');
  }

  function updateStep(index, changes) {
    const copy = steps.slice();
    copy[index] = Object.assign({}, copy[index], changes);
    saveSteps(copy);
  }

  function addScrollStep() {
    saveSteps(steps.concat([{
      action: 'scroll',
      selectorLabel: 'page',
      scrollDirection: 'down',
      scrollMode: 'viewport',
      scrollDistancePx: 600,
      scrollDurationMs: 600
    }]));
  }

  // AI Pointer: no picked element — at run time the AI reads the page's
  // clickable elements and rests the virtual mouse on the one matching
  // the step's instruction.
  function addAiPointerStep() {
    saveSteps(steps.concat([{
      action: 'aiPointer',
      instruction: ''
    }]));
  }

  function reorderStep(from, to) {
    if (from < 0 || to < 0 || from === to || from >= steps.length || to >= steps.length) return;
    const copy = steps.slice();
    const moved = copy.splice(from, 1)[0];
    copy.splice(to, 0, moved);
    saveSteps(copy);
  }

  // Browser nodes that run BEFORE this one (nearest first) — candidates to
  // inherit a tab from. With no explicit pick, Auto continues from the
  // nearest one (the last used tab), matching the runtime fallback.
  const otherBrowserNodes = (props.browserActionNodes || []).filter(function (n) {
    return n && n.id !== nodeId;
  });
  const sourceNode = otherBrowserNodes.find(function (n) { return n.id === continueFrom; }) || null;
  const autoSource = tabMode === 'continue' && !continueFrom && otherBrowserNodes.length
    ? otherBrowserNodes[0]
    : null;

  // Setup recording follows the same root as runtime tab inheritance.
  // Every node in that chain therefore reuses one live recorder tab.
  function recordingContext() {
    const byId = {};
    otherBrowserNodes.forEach(function (n) { byId[n.id] = n; });
    const visited = {};
    let current = {
      id: nodeId,
      url: url,
      tabMode: tabMode,
      // Auto resolves to the nearest previous browser node so recording
      // resumes the same tab the run would inherit.
      continueFrom: continueFrom || (autoSource ? autoSource.id : '')
    };
    let firstUrl = '';
    let rootId = nodeId;
    while (current && !visited[current.id]) {
      visited[current.id] = true;
      rootId = current.id || rootId;
      if (!firstUrl && String(current.url || '').trim()) firstUrl = String(current.url).trim();
      if (String(current.tabMode || 'new') !== 'continue') break;
      current = byId[current.continueFrom] || null;
    }
    return { rootId: rootId, url: firstUrl };
  }
  const recorder = recordingContext();
  const pickUrl = recorder.url;
  const recordingSessionId = 'node:' + recorder.rootId;
  const canPick = !!pickUrl && conn.connected && !picking;
  // Why the Pick button is disabled — shown under it so the user isn't
  // left staring at a dead control.
  const pickBlocker = picking ? '' : !pickUrl
    ? (tabMode === 'continue'
        ? ((sourceNode || autoSource)
            ? 'The "Continue from" node has no Webpage URL anywhere in its chain — enter a URL here to pick on.'
            : 'No previous browser node to continue from — enter a Webpage URL here so there is a page to pick the element on.')
        : 'Enter a Webpage URL first, so there is a page to pick the element on.')
    : !conn.connected ? 'Waiting for the Browser Action extension to connect…' : '';

  // Record a multi-step sequence: hover over each element in the opened
  // tab and choose its action; a floating button finishes the recording.
  function recordSteps(append) {
    if (!canPick) return;
    setPicking(true);
    setPickError('');
    apiClient.request('/browser-action/pick', {
      method: 'POST',
      body: { url: pickUrl, steps: true, recordingSessionId: recordingSessionId }
    })
      .then(function (res) {
        setPicking(false);
        if (res && res.ok && Array.isArray(res.steps) && res.steps.length) {
          saveSteps(append ? steps.concat(res.steps) : res.steps);
        } else {
          setPickError((res && res.error) || 'No steps were recorded.');
        }
      })
      .catch(function (err) {
        setPicking(false);
        setPickError(err && err.message ? err.message : 'Step recording failed.');
      });
  }

  // Re-pick ONE step's element: a normal single pick (not a recording) —
  // the tab opens, the user hovers over the element, and only this
  // step's selector/fingerprint are replaced. Left-clicks still work in
  // the tab, so mid-sequence pages can be reached by hand first.
  function repickStep(i) {
    if (!canPick || pickingStep >= 0) return;
    setPickingStep(i);
    setPickError('');
    const st = steps[i] || {};
    apiClient.request('/browser-action/pick', {
      method: 'POST',
      body: {
        url: pickUrl,
        action: String(st.action || ''),
        recordingSessionId: recordingSessionId
      }
    })
      .then(function (res) {
        setPickingStep(-1);
        if (res && res.ok && res.selector) {
          const copy = steps.slice();
          copy[i] = Object.assign({}, copy[i], {
            selector: res.selector,
            selectorLabel: res.selectorLabel || res.selector,
            fingerprint: res.fingerprint || null,
            multi: !!res.multi,
            // Always overwrite: a re-pick must clear a stale list choice too.
            matchMode: res.matchMode || '',
            matchSelector: res.matchSelector || '',
            matchTargetSelector: res.matchTargetSelector || ''
          });
          saveSteps(copy);
        } else {
          setPickError((res && res.error) || 'No element was selected.');
        }
      })
      .catch(function (err) {
        setPickingStep(-1);
        setPickError(err && err.message ? err.message : 'Element picking failed.');
      });
  }

  // Running section number, assigned in render order over the rows that are
  // actually visible so the badges read 1..N with no gaps.
  let step = 0;
  const next = function () { step += 1; return step; };

  return h('div', { className: 'wf-ba-form' },
    // 1 — Tab
    h(NumberedSection, { n: next(), label: 'Tab' },
      h('select', {
        className: 'wf-params-input',
        value: tabMode,
        onChange: function (e) { onParamChange('tabMode', e.target.value); }
      },
        h('option', { value: 'new' }, 'Open a new tab'),
        h('option', { value: 'continue' }, 'Continue from a previous node')
      ),
      tabMode === 'continue'
        ? (otherBrowserNodes.length > 0
            ? h(React.Fragment, null,
                h('select', {
                  className: 'wf-params-input',
                  value: continueFrom,
                  onChange: function (e) { onParamChange('continueFrom', e.target.value); }
                },
                  h('option', { value: '' }, 'Auto — use the last used tab'),
                  otherBrowserNodes.map(function (n) {
                    return h('option', { key: n.id, value: n.id },
                      n.title + (n.url ? ' — ' + n.url : ''));
                  })
                ),
                autoSource ? h('div', { className: 'wf-ba-conn-hint' },
                  'Continues from: ', autoSource.title) : null
              )
            : h('div', { className: 'wf-ba-conn-hint' },
                'No browser node runs before this one — a new tab will be opened.'))
        : null
    ),
    // 2 — Webpage URL (required unless a continue-from tab supplies the page)
    h(NumberedSection, {
      n: next(),
      label: 'Webpage URL',
      required: tabMode !== 'continue'
    },
      h(VariableAutocompleteInput, {
        value: url,
        placeholder: tabMode === 'continue'
          ? 'Leave empty to stay on the inherited tab’s page'
          : 'https://example.com · or @variable',
        variableNodes: variableNodes,
        onChange: function (next) { onParamChange('url', next); }
      })
    ),
    // Recorded multi-step sequence replaces the single Action/Target rows.
    steps.length ? h(NumberedSection, { n: next(), label: 'Steps' },
      h('div', { className: 'wf-ba-steps' },
        steps.map(function (st, i) {
          const stepAction = String(st.action || 'click');
          const stepDetail = stepAction === 'scroll'
            ? describeScrollStep(st)
            : stepAction === 'aiPointer'
              ? (String(st.instruction || '').trim() || 'AI picks the element')
              : (st.selectorLabel || st.selector || '');
          return h('div', {
            key: i,
            className: 'wf-ba-step' + (dragOverStep === i && draggedStep !== i ? ' wf-ba-step-drag-over' : ''),
            onDragOver: function (e) {
              if (draggedStep < 0) return;
              e.preventDefault();
              setDragOverStep(i);
            },
            onDrop: function (e) {
              e.preventDefault();
              reorderStep(draggedStep, i);
              setDraggedStep(-1);
              setDragOverStep(-1);
            }
          },
            h('div', { className: 'wf-ba-step-head' },
              h('span', {
                className: 'wf-ba-step-drag',
                draggable: true,
                title: 'Drag to reorder step',
                'aria-label': 'Drag to reorder step ' + (i + 1),
                onDragStart: function (e) {
                  setDraggedStep(i);
                  setDragOverStep(i);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(i));
                },
                onDragEnd: function () {
                  setDraggedStep(-1);
                  setDragOverStep(-1);
                }
              }, '⋮⋮'),
              h(StepActionIcon, { action: stepAction }),
              h('span', { className: 'wf-ba-step-text' },
                (i + 1) + '. ' + (STEP_LABELS[stepAction] || stepAction) + ' — ' + stepDetail),
              stepAction !== 'screenshot' && stepAction !== 'scroll' && stepAction !== 'aiPointer' && stepAction !== 'clickIfText' ? h('button', {
                type: 'button',
                className: 'wf-ba-step-btn',
                title: 'Pick this step’s element again (single pick, not a re-recording)',
                disabled: !canPick || pickingStep >= 0,
                onClick: function () { repickStep(i); }
              }, pickingStep === i ? 'Picking…' : 'Re-pick') : null,
              h('button', {
                type: 'button',
                className: 'wf-ba-step-btn wf-ba-step-remove',
                title: 'Remove step',
                onClick: function () {
                  const nextSteps = steps.slice();
                  nextSteps.splice(i, 1);
                  saveSteps(nextSteps);
                }
              }, '✕')
            ),
            (stepAction === 'type' || stepAction === 'typeEnter') ? h('div', { style: { marginTop: 7 } },
              h(VariableAutocompleteInput, {
                value: String(st.value || ''),
                placeholder: 'Text to type — text or @variable',
                variableNodes: variableNodes,
                onChange: function (nextVal) {
                  updateStep(i, { value: nextVal });
                }
              }),
              h('div', { className: 'wf-ba-step-sub' },
                h('span', { className: 'wf-ba-step-sub-label' }, 'Typing delay (ms per character)'),
                h('input', {
                  className: 'wf-params-input',
                  type: 'number',
                  min: 0,
                  value: st.typingDelayMs !== undefined && st.typingDelayMs !== '' ? st.typingDelayMs : '',
                  placeholder: 'Natural',
                  title: 'Leave blank to use the natural randomized typing speed',
                  style: { width: 100 },
                  onChange: function (e) {
                    updateStep(i, { typingDelayMs: e.target.value });
                  }
                })
              )
            ) : null,
            stepAction === 'clickIfText' ? h('div', { style: { marginTop: 7 } },
              h(VariableAutocompleteInput, {
                value: String(st.value || ''),
                placeholder: 'Row must contain this text — text or @variable',
                variableNodes: variableNodes,
                onChange: function (nextVal) {
                  updateStep(i, { value: nextVal });
                }
              }),
              h('div', { className: 'wf-ba-step-sub' },
                h('span', { className: 'wf-ba-step-sub-label' }, 'Row: ' + (st.selectorLabel || st.selector || ''))),
              h('div', { className: 'wf-ba-step-sub' },
                h('span', { className: 'wf-ba-step-sub-label' }, 'Check text in: ' + (st.conditionLabel || 'the row itself'))),
              h('div', { className: 'wf-ba-step-sub' },
                h('span', { className: 'wf-ba-step-sub-label' }, 'Click: ' + (st.targetLabel || 'the row itself'))),
              h('div', { className: 'wf-ba-step-hint' },
                'Clicks the target inside the FIRST row whose checked text contains the value (case-insensitive). Fails if no row matches.')
            ) : null,
            stepAction === 'aiPointer' ? h('div', { style: { marginTop: 7 } },
              h(VariableAutocompleteInput, {
                value: String(st.instruction || ''),
                placeholder: 'What should the pointer rest on? e.g. "the checkout button" · or @variable',
                variableNodes: variableNodes,
                onChange: function (nextVal) {
                  updateStep(i, { instruction: nextVal });
                }
              }),
              h('div', { className: 'wf-ba-step-hint' },
                'At run time the AI scans the page’s clickable elements and moves the virtual mouse onto the best match. It does not click.')
            ) : null,
            stepAction === 'screenshot' ? h('label', {
              className: 'wf-ba-check',
              style: { marginTop: 10 }
            },
              h('input', {
                type: 'checkbox',
                checked: st.fullPage === true || String(st.fullPage || '') === 'true',
                onChange: function (e) {
                  updateStep(i, { fullPage: e.target.checked });
                }
              }),
              'Capture full page as separate screenshots'
            ) : null,
            stepAction === 'scroll' ? h(ScrollStepFields, {
              step: st,
              onChange: function (changes) { updateStep(i, changes); }
            }) : null,
            h('div', { className: 'wf-ba-step-sub' },
              h('span', { className: 'wf-ba-step-sub-label' }, 'Delay before step (ms)'),
              h('input', {
                className: 'wf-params-input',
                type: 'number',
                min: 0,
                value: st.delayMs !== undefined && st.delayMs !== '' ? st.delayMs : '',
                placeholder: i === 0 ? '0' : '300',
                style: { width: 100 },
                onChange: function (e) {
                  updateStep(i, { delayMs: e.target.value });
                }
              })
            ),
            h('label', { className: 'wf-ba-check', style: { marginTop: 7 } },
              h('input', {
                type: 'checkbox',
                checked: st.skipOnFail === true || String(st.skipOnFail || '') === 'true',
                onChange: function (e) {
                  updateStep(i, { skipOnFail: e.target.checked });
                }
              }),
              'Skip this step if it fails'
            )
          );
        }),
        h('button', {
          type: 'button',
          className: 'wf-ba-record-btn',
          onClick: addScrollStep
        }, '＋ Add scroll step'),
        h('button', {
          type: 'button',
          className: 'wf-ba-record-btn',
          onClick: addAiPointerStep
        }, '＋ Add AI pointer step'),
        h('button', {
          type: 'button',
          className: 'wf-ba-record-btn',
          disabled: !canPick,
          onClick: function () { recordSteps(true); }
        }, picking ? 'Recording… hover over elements in the opened tab' : '＋ Resume adding steps'),
        h('div', { className: 'wf-ba-step-hint' },
          tabMode === 'continue'
            ? 'Recording resumes the previous Browser Action’s live setup tab and appends new steps.'
            : 'This setup tab stays open so you can resume recording or continue from it in another Browser Action.'
        ),
        h('button', {
          type: 'button',
          className: 'wf-ba-text-btn',
          onClick: function () { saveSteps([]); }
        }, 'Clear steps'),
        pickError ? h('div', { className: 'wf-ba-error' }, pickError) : null
      )
    ) : null,
    // 3 — Steps empty state: recording IS how the node is configured.
    steps.length === 0 ? h(NumberedSection, { n: next(), label: 'Steps', required: true },
      h('button', {
        type: 'button',
        className: 'wf-ba-record-btn',
        disabled: !canPick,
        onClick: function () { recordSteps(false); }
      }, picking ? 'Recording… hover over elements in the opened tab' : '⏺ Record steps'),
      h('button', {
        type: 'button',
        className: 'wf-ba-record-btn',
        onClick: addScrollStep
      }, '＋ Add scroll step'),
      h('button', {
        type: 'button',
        className: 'wf-ba-record-btn',
        onClick: addAiPointerStep
      }, '＋ Add AI pointer step'),
      !canPick && pickBlocker ? h('div', { className: 'wf-ba-notice' },
        h('span', { className: 'wf-ba-notice-icon' }, '⚠'),
        h('span', null, pickBlocker)
      ) : null,
      pickError ? h('div', { className: 'wf-ba-error' }, pickError) : null,
      h('div', { className: 'wf-ba-conn-hint', style: { marginTop: 6 } },
        'In the opened tab: hover over an element, then choose its action (click, type, extract…) from the balloon. The recording dock adds screenshot steps and finishes the recording. A node with just a URL and no steps simply opens the page.')
    ) : null,
    // ---- OPTIONAL group ----
    h('div', { className: 'wf-ba-optional' },
      h('div', { className: 'wf-ba-optional-label' }, 'Optional'),
      h('div', null,
        h('label', { className: 'wf-ba-check' },
          h('input', {
            type: 'checkbox',
            checked: closeTab,
            onChange: function (e) { onParamChange('closeTab', e.target.checked ? 'true' : ''); }
          }),
          'Close the tab when this node finishes'
        ),
        closeTab ? h('div', { className: 'wf-ba-check-hint' },
          'Don’t check this on a node that other Browser Action nodes continue from — they’d lose the tab.'
        ) : null
      )
    ),
    !conn.connected && conn.checked ? h('div', { className: 'wf-ba-conn-hint', style: { marginTop: 14 } },
      'Browser Action extension not connected. Load browser_action_ext in Chrome (chrome://extensions) and keep Chrome open.'
    ) : null
  );
}

module.exports = BrowserActionEditor;
